// ─────────────────────────────────────────────────────────────────────────
// useGeolocation.ts
// Ports getGeolocation(callback) from the original script: caches the first
// successful (or fallback) fix for the page lifetime, and pre-warms it on
// mount the same way `window.addEventListener('load', ...)` did.
//
// NOTE: The driver job-sheet flow (OnlineJobSheetPage.tsx) no longer uses
// `useGeolocation()`/`getCoords()` to stamp auditStartGps/auditEndGps —
// it uses the real, continuously-tracked position from
// useLiveLocationBroadcast.ts instead, precisely because getCoords()'s
// silent FALLBACK_COORDS below (a fixed Bangkok point) could otherwise get
// written as if it were the driver's real position when GPS was denied or
// unavailable. Only `parseGpsString` from this file is still used
// elsewhere (AuditLogSection, for display purposes).
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef } from 'react';
import type { GpsCoords } from './jobSheet.types';

// Fallback coordinates used by the original when geolocation is unavailable
// or denied (Bangkok city-center placeholder).
const FALLBACK_COORDS: GpsCoords = { lat: '13.689814', lng: '100.750112' };

export interface UseGeolocationResult {
  /** Resolves with cached coords if available, otherwise requests a fresh fix (or falls back). */
  getCoords: () => Promise<GpsCoords>;
}

export function useGeolocation(): UseGeolocationResult {
  const cachedRef = useRef<GpsCoords | null>(null);

  const getCoords = useCallback((): Promise<GpsCoords> => {
    if (cachedRef.current) return Promise.resolve(cachedRef.current);

    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) {
        cachedRef.current = FALLBACK_COORDS;
        resolve(FALLBACK_COORDS);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords: GpsCoords = {
            lat: pos.coords.latitude.toFixed(6),
            lng: pos.coords.longitude.toFixed(6),
          };
          cachedRef.current = coords;
          resolve(coords);
        },
        () => {
          cachedRef.current = FALLBACK_COORDS;
          resolve(FALLBACK_COORDS);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 },
      );
    });
  }, []);

  // Pre-warm permission/cache on mount, mirroring the original's
  // window 'load' listener that calls getGeolocation(() => {}).
  useEffect(() => {
    void getCoords();
  }, [getCoords]);

  return { getCoords };
}

/** Parses a stored "lat, lng" string into a GpsCoords pair (for restoring audit logs). */
export function parseGpsString(gps: string | null | undefined): GpsCoords | null {
  if (!gps) return null;
  const [lat, lng] = gps.split(',').map((s) => s.trim());
  if (!lat || !lng) return null;
  return { lat, lng };
}

export function formatGpsString(coords: GpsCoords): string {
  return `${coords.lat}, ${coords.lng}`;
}
